import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ToggleLeft, ToggleRight, ShieldCheck, ShieldOff, Mail, UserCheck, Copy, ExternalLink, AlertTriangle, LogIn } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import TutorPendingChangesPanel from "./TutorPendingChangesPanel";
import TutorEnquiryVisibilitySettings from "./TutorEnquiryVisibilitySettings";

/**
 * V25 Phase 1 — Admin Tutor Portal Settings.
 *   - Master ON/OFF toggle for the Tutor Portal.
 *   - List of tutor applications awaiting account provisioning.
 *   - "Approve & provision" action — backend emails temp password to the tutor.
 */
export default function TutorPortalSettings() {
    const [loading, setLoading] = useState(true);
    const [enabled, setEnabled] = useState(false);
    const [pwaVisible, setPwaVisible] = useState(false);
    const [pwaBusy, setPwaBusy] = useState(false);
    const [loginButtonVisible, setLoginButtonVisible] = useState(false);
    const [loginButtonBusy, setLoginButtonBusy] = useState(false);
    const [meta, setMeta] = useState({ updated_at: null, updated_by: null, accounts_total: 0, accounts_active: 0 });
    const [pwaMeta, setPwaMeta] = useState({ updated_at: null, updated_by: null });
    const [loginButtonMeta, setLoginButtonMeta] = useState({ updated_at: null, updated_by: null });
    const [toggleBusy, setToggleBusy] = useState(false);
    const [pending, setPending] = useState([]);
    const [pendingLoading, setPendingLoading] = useState(true);
    const [approving, setApproving] = useState(null);
    const [provisioned, setProvisioned] = useState(null); // { email, temp_password }

    const fetchSettings = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/tutor-portal/settings");
            setEnabled(!!data.enabled);
            setPwaVisible(!!data.pwa_download_visible);
            setLoginButtonVisible(!!data.login_button_visible);
            setMeta({
                updated_at: data.setting?.updated_at || null,
                updated_by: data.setting?.updated_by || null,
                accounts_total: data.accounts_total || 0,
                accounts_active: data.accounts_active || 0,
            });
            setPwaMeta({
                updated_at: data.pwa_download_setting?.updated_at || null,
                updated_by: data.pwa_download_setting?.updated_by || null,
            });
            setLoginButtonMeta({
                updated_at: data.login_button_setting?.updated_at || null,
                updated_by: data.login_button_setting?.updated_by || null,
            });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally { setLoading(false); }
    }, []);

    const fetchPending = useCallback(async () => {
        setPendingLoading(true);
        try {
            const { data } = await api.get("/admin/tutor-portal/pending-applications?limit=50");
            setPending(data.items || []);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally { setPendingLoading(false); }
    }, []);

    useEffect(() => { fetchSettings(); fetchPending(); }, [fetchSettings, fetchPending]);

    const toggleEnabled = async () => {
        const next = !enabled;
        const ok = window.confirm(
            next
                ? "Turn the Tutor Portal ON?\n\nThe Tutor Login link will become publicly accessible immediately."
                : "Turn the Tutor Portal OFF?\n\nAll /tutor/* routes will become unavailable. Existing accounts stay intact."
        );
        if (!ok) return;
        setToggleBusy(true);
        try {
            const { data } = await api.post("/admin/tutor-portal/settings", { enabled: next });
            setEnabled(!!data.enabled);
            toast.success(`Tutor Portal turned ${data.enabled ? "ON" : "OFF"}.`);
            fetchSettings();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally { setToggleBusy(false); }
    };

    const togglePwaDownload = async () => {
        const next = !pwaVisible;
        const ok = window.confirm(
            next
                ? "Show the 'Download Tutor App' button on the public website?\n\nApproved tutors will see it and can install the Tutor PWA."
                : "Hide the 'Download Tutor App' button?\n\nThe public site will no longer surface the install button."
        );
        if (!ok) return;
        setPwaBusy(true);
        try {
            const { data } = await api.post("/admin/tutor-portal/pwa-download", { visible: next });
            setPwaVisible(!!data.pwa_download_visible);
            toast.success(`Download button ${data.pwa_download_visible ? "SHOWN" : "HIDDEN"}.`);
            fetchSettings();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally { setPwaBusy(false); }
    };

    const toggleLoginButton = async () => {
        const next = !loginButtonVisible;
        const ok = window.confirm(
            next
                ? "Show the 'Tutor Login' link on the public website footer?\n\nThe direct /tutor/login URL works either way."
                : "Hide the 'Tutor Login' link from the public website footer?\n\nThe direct /tutor/login URL keeps working — only the visible link is hidden."
        );
        if (!ok) return;
        setLoginButtonBusy(true);
        try {
            const { data } = await api.post("/admin/tutor-portal/login-button", { visible: next });
            setLoginButtonVisible(!!data.login_button_visible);
            toast.success(`Tutor Login button ${data.login_button_visible ? "SHOWN" : "HIDDEN"}.`);
            fetchSettings();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally { setLoginButtonBusy(false); }
    };

    const approve = async (appId, email) => {
        if (!window.confirm(`Provision a tutor portal account for ${email}?\n\nA welcome email with a temporary password will be sent.`)) return;
        setApproving(appId);
        try {
            const { data } = await api.post("/admin/tutor-portal/approve", { application_id: appId, send_welcome_email: true });
            toast.success(`Account created for ${data.account?.email}`);
            setProvisioned({ email: data.account?.email, temp_password: data.temp_password });
            fetchPending();
            fetchSettings();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally { setApproving(null); }
    };

    return (
        <div className="space-y-6" data-testid="tutor-portal-settings">
            {/* Master ON/OFF */}
            <section className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                        <h2 className="font-bold text-lg text-[#0F172A] flex items-center gap-2">
                            {enabled ? <ShieldCheck className="w-5 h-5 text-emerald-600" /> : <ShieldOff className="w-5 h-5 text-amber-600" />}
                            Tutor Portal Status
                        </h2>
                        <p className="text-sm text-slate-600 mt-1">
                            When ON, tutors can log in at <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">/tutor/login</code>.
                            When OFF, all /tutor/* routes return a friendly maintenance page and existing accounts stay intact.
                        </p>
                        <div className="mt-3 text-xs text-slate-500 space-x-3" data-testid="tutor-portal-meta">
                            <span>Accounts: <strong>{meta.accounts_active}/{meta.accounts_total} active</strong></span>
                            {meta.updated_at && <span>Last changed: {new Date(meta.updated_at).toLocaleString()}</span>}
                            {meta.updated_by && <span>by {meta.updated_by}</span>}
                        </div>
                    </div>
                    <button
                        onClick={toggleEnabled}
                        disabled={loading || toggleBusy}
                        data-testid="tutor-portal-toggle"
                        data-enabled={enabled ? "true" : "false"}
                        className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition shadow-sm
                            ${enabled
                                ? "bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-300"
                                : "bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-300"}`}
                    >
                        {toggleBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : enabled ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                        Portal {enabled ? "ON" : "OFF"}
                    </button>
                </div>
                {!enabled && (
                    <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-900 inline-flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                        Portal is currently hidden from the public. Flip the toggle once you&rsquo;re ready to launch — no redeploy needed.
                    </div>
                )}
            </section>

            {/* Tutor PWA Download Button — independent toggle */}
            <section className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm" data-testid="tutor-pwa-download-section">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                        <h2 className="font-bold text-lg text-[#0F172A] flex items-center gap-2">
                            {pwaVisible ? <ShieldCheck className="w-5 h-5 text-emerald-600" /> : <ShieldOff className="w-5 h-5 text-slate-400" />}
                            Tutor PWA Download Button
                        </h2>
                        <p className="text-sm text-slate-600 mt-1">
                            Controls the visibility of the &ldquo;Download Tutor App&rdquo; button on the public website.
                            <strong> This setting is independent of the portal toggle</strong> — registration and email flows are never affected by either.
                        </p>
                        <div className="mt-3 text-xs text-slate-500 space-x-3" data-testid="tutor-pwa-download-meta">
                            {pwaMeta.updated_at && <span>Last changed: {new Date(pwaMeta.updated_at).toLocaleString()}</span>}
                            {pwaMeta.updated_by && <span>by {pwaMeta.updated_by}</span>}
                        </div>
                    </div>
                    <button
                        onClick={togglePwaDownload}
                        disabled={loading || pwaBusy}
                        data-testid="tutor-pwa-download-toggle"
                        data-visible={pwaVisible ? "true" : "false"}
                        className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition shadow-sm
                            ${pwaVisible
                                ? "bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-300"
                                : "bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-300"}`}
                    >
                        {pwaBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : pwaVisible ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                        Download Button {pwaVisible ? "SHOWN" : "HIDDEN"}
                    </button>
                </div>
            </section>

            {/* Tutor Login Button on public site — independent toggle */}
            <section className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm" data-testid="tutor-login-button-section">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                        <h2 className="font-bold text-lg text-[#0F172A] flex items-center gap-2">
                            {loginButtonVisible ? <LogIn className="w-5 h-5 text-emerald-600" /> : <LogIn className="w-5 h-5 text-slate-400" />}
                            Tutor Login Button (Public Site Footer)
                        </h2>
                        <p className="text-sm text-slate-600 mt-1">
                            Controls the visibility of the &ldquo;Tutor Login&rdquo; link in the public website footer.
                            <strong> Hiding this does NOT block tutor logins</strong> — tutors with the
                            direct <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">/tutor/login</code> URL can still sign in.
                            Independent of the Portal Status and PWA Download toggles.
                        </p>
                        <div className="mt-3 text-xs text-slate-500 space-x-3" data-testid="tutor-login-button-meta">
                            {loginButtonMeta.updated_at && <span>Last changed: {new Date(loginButtonMeta.updated_at).toLocaleString()}</span>}
                            {loginButtonMeta.updated_by && <span>by {loginButtonMeta.updated_by}</span>}
                        </div>
                    </div>
                    <button
                        onClick={toggleLoginButton}
                        disabled={loading || loginButtonBusy}
                        data-testid="tutor-login-button-toggle"
                        data-visible={loginButtonVisible ? "true" : "false"}
                        className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition shadow-sm
                            ${loginButtonVisible
                                ? "bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-300"
                                : "bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-300"}`}
                    >
                        {loginButtonBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : loginButtonVisible ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                        Tutor Login Button {loginButtonVisible ? "SHOWN" : "HIDDEN"}
                    </button>
                </div>
            </section>

            {/* Tutor Enquiry Visibility — Group A items 1, 6, 7 */}
            <TutorEnquiryVisibilitySettings />


            {/* Pending applications */}
            <section className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="font-bold text-lg text-[#0F172A]">Approve tutors for portal access</h2>                    <button onClick={fetchPending} disabled={pendingLoading} className="text-xs text-[#1E3A8A] hover:underline" data-testid="tutor-portal-refresh-pending">
                        {pendingLoading ? "Refreshing…" : "Refresh"}
                    </button>
                </div>
                {pendingLoading ? (
                    <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading applications…</div>
                ) : pending.length === 0 ? (
                    <div className="text-sm text-slate-500 py-4 text-center border-2 border-dashed border-slate-200 rounded-lg" data-testid="tutor-portal-pending-empty">
                        No pending applications. All tutor applications are either already provisioned or have no email on file.
                    </div>
                ) : (
                    <div className="space-y-2" data-testid="tutor-portal-pending-list">
                        {pending.map((it) => (
                            <div key={it.id} data-testid={`tutor-portal-pending-${it.id}`}
                                className="flex items-center justify-between gap-3 border border-slate-200 rounded-xl px-3 py-2.5 hover:bg-slate-50">
                                <div className="min-w-0">
                                    <div className="font-semibold text-sm text-[#0F172A] truncate">{it.name || "—"}</div>
                                    <div className="text-xs text-slate-500 truncate">
                                        {it.email} · {it.city || "—"} · {it.subjects || "—"} · {it.mode || "—"}
                                    </div>
                                </div>
                                <button onClick={() => approve(it.id, it.email)} disabled={approving === it.id}
                                    data-testid={`tutor-portal-approve-${it.id}`}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1E3A8A] hover:bg-[#1E40AF] text-white text-xs font-semibold disabled:opacity-60">
                                    {approving === it.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserCheck className="w-3.5 h-3.5" />}
                                    Approve &amp; provision
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* V25 Phase 2 — pending profile changes (sensitive fields) */}
            <TutorPendingChangesPanel />

            {/* Provisioned credential modal */}
            {provisioned && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4">
                    <div className="bg-white rounded-2xl w-full max-w-md p-5 shadow-xl" data-testid="tutor-portal-provisioned-modal">
                        <h3 className="font-bold text-lg text-[#0F172A] mb-1 flex items-center gap-2">
                            <Mail className="w-5 h-5 text-emerald-600" /> Welcome email sent
                        </h3>
                        <p className="text-sm text-slate-600 mb-4">
                            The tutor has been emailed a welcome message with their temporary password. They&rsquo;ll be prompted to change it on first login.
                        </p>
                        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs font-mono space-y-1">
                            <div><strong>Email:</strong> {provisioned.email}</div>
                            <div className="flex items-center gap-2">
                                <strong>Temp:</strong> {provisioned.temp_password}
                                <button onClick={() => { navigator.clipboard.writeText(provisioned.temp_password); toast.success("Copied"); }}
                                    className="inline-flex items-center gap-0.5 text-[#1E3A8A] hover:underline text-[11px]">
                                    <Copy className="w-3 h-3" /> Copy
                                </button>
                            </div>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-3">
                            Share this temp password verbally if the tutor doesn&rsquo;t receive the email within 10 minutes.
                        </p>
                        <div className="flex items-center justify-end gap-2 mt-4">
                            <a href="/tutor/login" target="_blank" rel="noreferrer" className="text-xs text-[#1E3A8A] hover:underline inline-flex items-center gap-1">
                                Open tutor login <ExternalLink className="w-3 h-3" />
                            </a>
                            <button onClick={() => setProvisioned(null)} className="px-3 py-1.5 rounded-lg bg-[#1E3A8A] hover:bg-[#1E40AF] text-white text-xs font-semibold"
                                data-testid="tutor-portal-provisioned-close">Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
